import { Navigate, Route, Routes, useMatch } from 'react-router-dom'
import './App.css'
import { type SessionState, useSession } from './auth/SessionProvider'
import { ApplicationList, useApplicationPage } from './applications/ApplicationList'
import { AppShell } from './shell/AppShell'

/**
 * Where the BFF sends an authenticated person who is not approved.
 *
 * Specified by contracts/console/openapi.yaml, not chosen here.
 */
const DENIED_PATH = '/access-denied'

/**
 * Still working. Announced rather than drawn, because a spinner nobody can
 * hear is a blank page to a screen reader. Polite, not assertive: it is not
 * worth interrupting what someone is reading.
 */
function Loading({ what }: { what: string }) {
  return (
    <p className="notice" role="status" aria-live="polite">
      Loading {what}…
    </p>
  )
}

/**
 * The signed-in person is authenticated and not approved. A distinct state
 * from signed-out: telling someone to sign in when they already have would
 * send them round a loop that cannot end.
 */
function AccessDenied() {
  return (
    <>
      <h2>Access denied</h2>
      <p className="notice is-bad" role="alert">
        The Horai console is open only to approved accounts. If you signed in and expected access,
        ask an administrator.
      </p>
    </>
  )
}

/**
 * A dependency could not answer. Deliberately not phrased as an empty result:
 * zero applications and "we could not look" are different facts, the console
 * shows them differently, and a CISO decides differently on each.
 */
function ServiceUnavailable() {
  return (
    <>
      <h2>Service unavailable</h2>
      <p className="notice is-bad" role="alert">
        The findings service could not be reached, so this page cannot say what is installed. This
        is not a report that nothing was found.
      </p>
    </>
  )
}

/**
 * A tab the console draws and does not fill, because nothing collects it.
 *
 * Endpoints, Integrations and Identities are in the design and have no source
 * behind them. Saying so is the honest state; a zero would be a measurement
 * nobody took.
 */
function NotCollected({ what }: { what: string }) {
  return (
    <p className="notice">
      The console does not collect {what} yet, so there is nothing to show here. This is not a
      report that none were found.
    </p>
  )
}

/**
 * A tab whose data exists and has not been wired up yet.
 *
 * Different from NotCollected, and the difference is the point: this data is
 * collected and stored, the console simply does not read it yet.
 *
 * Deliberately not a loading state. Nothing is in flight, so "Loading
 * applications…" would spin forever and claim work that is not happening -
 * which is the same false-reassurance failure as reporting an empty result
 * when a collector could not look.
 */
function NotConnected({ what }: { what: string }) {
  return (
    <p className="notice">
      {what} are collected, and the console does not read them yet. Nothing here is a count.
    </p>
  )
}

export function App() {
  const { state } = useSession()

  // Before the session gate on purpose. The callback sends an unapproved
  // person here with no session, so a page reachable only when signed in
  // would offer them sign-in instead - the loop AccessDenied exists to
  // prevent. It carries no data, so showing it to anyone costs nothing.
  const denied = useMatch(DENIED_PATH)
  if (denied && state.status === 'signedIn') {
    return <Navigate to="/inventory/apps" replace />
  }
  if (denied) {
    return (
      <Gate>
        <AccessDenied />
      </Gate>
    )
  }

  if (state.status !== 'signedIn') {
    return <NotSignedIn state={state} />
  }

  return (
    <Routes>
      {/* The root is not a page, and neither is a bare /inventory: landing
          somewhere concrete is what stops a bookmark of one from being a
          different experience to a bookmark of the page it lands on. */}
      <Route path="/" element={<Navigate to="/inventory/apps" replace />} />
      <Route path="/inventory" element={<Navigate to="/inventory/apps" replace />} />

      {/* One tab per URL. A tab held in component state cannot be bookmarked,
          shared, or reached with Back. */}
      <Route path="/inventory/apps" element={<AppsTab />} />
      <Route
        path="/inventory/endpoints"
        element={<Inventory>{<NotConnected what="Endpoints" />}</Inventory>}
      />
      <Route
        path="/inventory/integrations"
        element={<Inventory>{<NotCollected what="integrations" />}</Inventory>}
      />
      <Route
        path="/inventory/identities"
        element={<Inventory>{<NotCollected what="identities" />}</Inventory>}
      />

      <Route
        path="*"
        element={
          <AppShell title="Page not found" footer={<SignOut />}>
            <p className="notice">That address is not part of the console.</p>
          </AppShell>
        }
      />
    </Routes>
  )
}

/** The Inventory surface: the shell, its tab bar, and whichever tab is open. */
function Inventory({
  counts,
  children,
}: {
  counts?: Readonly<Record<string, number | undefined>>
  children: React.ReactNode
}) {
  return (
    <AppShell title="Inventory" tabs footer={<SignOut />} {...(counts ? { counts } : {})}>
      {children}
    </AppShell>
  )
}

/** The Apps tab, with its real total once it has been read. */
function AppsTab() {
  const { data } = useApplicationPage()
  // unknown carries a zero total that is not a count of anything.
  const total = data?.page.result === 'unknown' ? undefined : data?.page.total_count
  return (
    <Inventory counts={{ Apps: total }}>
      <ApplicationList />
    </Inventory>
  )
}

/**
 * The way out.
 *
 * A button, not a link: signing out is a state change that posts a CSRF token,
 * and the redirect to Cognito is the server's answer rather than this href.
 * No name beside it - the design shows none, and the one the console can
 * currently render is an opaque subject id.
 */
function SignOut() {
  const { signOut } = useSession()

  return (
    <button type="button" className="rail-signout" onClick={() => void signOut()}>
      Sign out
    </button>
  )
}

/**
 * Every state that is not signed in. Each is a different fact and gets a
 * different page: denied is not signed-out, and unavailable is neither.
 *
 * None of them gets the shell. Navigation a person cannot use is an
 * invitation to bounce, and the console's chrome would imply they are inside
 * something they have not been let into.
 */
function NotSignedIn({ state }: { state: Exclude<SessionState, { status: 'signedIn' }> }) {
  return <Gate>{gateContent(state)}</Gate>
}

/** The chrome-free card every pre-console page is shown in. */
function Gate({ children }: { children: React.ReactNode }) {
  return (
    <div className="gate">
      <main className="gate-card">
        <h1 className="wordmark">
          <span aria-hidden="true">Horai</span>
          <span className="sr-only">Horai Console</span>
        </h1>
        {children}
      </main>
    </div>
  )
}

function gateContent(state: Exclude<SessionState, { status: 'signedIn' }>) {
  switch (state.status) {
    case 'loading':
      return <Loading what="your session" />
    case 'signedOut':
      return <SignIn loginUrl={state.loginUrl} />
    case 'denied':
      return <AccessDenied />
    case 'unavailable':
      return <ServiceUnavailable />
  }
}

/**
 * Sends the browser to Cognito.
 *
 * A location assignment, not a Link or a fetch: sign-in is a top-level
 * navigation away from this origin, and fetch would follow the redirect
 * itself and never hand the browser to Cognito.
 */
function SignIn({ loginUrl }: { loginUrl: string }) {
  return (
    <>
      <h2>Sign in</h2>
      {/* What the console is, rather than a third way of saying "sign in".
          The heading, this line and the button all said the same thing, and
          none of them told a first-time visitor what they were signing in to. */}
      <p className="gate-lead">
        The application inventory for the Horai fleet: what is installed, where, and what the rules
        say about it.
      </p>
      <a className="button" href={loginUrl}>
        Sign in with your Horai account
      </a>
    </>
  )
}
